import AlterSwab from '@public/alter-swab-vertical.png';
import Clock from '@public/icons/clock.svg';
import Fire from '@public/icons/fire-red.svg';
import ForkKnife from '@public/icons/fork-and-knife.svg';
import HeartRate from '@public/icons/heart-rate.svg';
import Lightning from '@public/icons/lightning-green.svg';
import Scale from '@public/icons/scale.svg';
import clsx from 'clsx';
import { motion, useAnimate, useInView } from 'framer-motion';
import Image from 'next/image';
import React, { useEffect, useRef } from 'react';

const MotionImage = motion(Image);

const CARDS_TEXT = [
  {
    icon: Scale,
    alt: 'weight loss',
    title: 'Insight',
    text: (
      <div className="text-2xl">
        Stress can increase belly fat <span className="text-[#7B84DC]">by 53%</span>
      </div>
    ),
  },
  {
    icon: ForkKnife,
    alt: 'eating the wrong foods',
    title: 'Insight',
    text: (
      <div className="text-2xl">
        Eating according to your genetics is <span className="text-[#BD7BDC]">194%</span> more
        effective than popular diets
      </div>
    ),
  },
  {
    icon: Clock,
    alt: 'lack of sleep',
    title: 'Insight',
    text: (
      <div className="text-2xl">
        <span className="text-[#C25C47]">88%</span> of failed fitness attempts is due to lack of
        sleep
      </div>
    ),
  },
  {
    icon: HeartRate,
    alt: 'enhance fat loss',
    title: 'Insight',
    text: (
      <div className="text-2xl">
        DNA based heart rate training can enhance fat loss by{' '}
        <span className="text-[#7AD9D2]">35%</span>
      </div>
    ),
  },
];

interface DrivenByScienceProps {
  // Define any props here if needed
}

const DrivenByScience: React.FC<DrivenByScienceProps> = (props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [swabRef, animate] = useAnimate();
  const isInView = useInView(containerRef, {
    amount: 0.75,
    once: true,
  });

  useEffect(() => {
    if (isInView) {
      animate(
        swabRef.current,
        { opacity: 1, translateY: 0 },
        {
          duration: 1,
          ease: 'easeOut',
        }
      );
    }
  }, [isInView]);

  return (
    <section className="py-11 px-4 bg-white font-light md:px-20 md:py-28">
      <div className="width-1440">
        <h2 className="max-w-[15ch] mx-auto font-light text-[36px] text-center leading-snug sm:max-w-none md:text-5xl">
          Driven By Science Not Hype
        </h2>
        <p className="max-w-[62ch] mx-auto text-center mt-5 text-alter-black-80 md:mt-6 md:text-xl">
          Your activity, sleep quality, stress levels and more define your fitness and wellness
          journey. By measuring those biometrics in real-time, Alter personalizes every workout, or
          restorative work-in to your needs.
        </p>
        <div className="md:mt-20 lg:flex lg:gap-8">
          <div className="flex flex-col justify-between mt-11 rounded-3xl bg-alter-beige overflow-hidden md:basis-1/2 md:mt-0">
            <div className="py-12 px-6">
              <div className="uppercase text-alter-black-60">First, we unpack your dna</div>
              <h3 className="mt-2 font-light text-[32px] text-alter-black leading-tight md:text-[40px]">
                At-home genetic testing
              </h3>
              <p className="mt-4 text-alter-black-80">
                This is where the guesswork ends. Find out what your body needs through your DNA in
                the comfort of your own home.
              </p>
            </div>
            <div
              ref={containerRef}
              className={clsx(
                "relative flex flex-col items-center h-[300px] w-full bg-[url('../../../public/gradients/why-alter-light-blue.webp')] bg-cover font-normal",
                'md:flex-grow'
              )}
            >
              <div
                className={clsx(
                  'absolute top-8 left-3 py-2 px-3 bg-white rounded-[4px]',
                  'md:top-10 md:left-11 md:py-4 md:px-6 md:rounded-lg'
                )}
              >
                <div className="text-[10px] text-alter-black md:text-sm">
                  Jane’s Genetic Systemic Inflammation
                </div>
                <div className="uppercase text-[8px] text-alter-black-60 md:text-xs md:mt-1">
                  DNA SNP: rs1175544, rs10830963, +2
                </div>
                <div className="flex gap-1 mt-2 text-[8px] md:text-sm md:gap-2 md:mt-5">
                  <Image src={Fire} alt="fire" width={9} height={10} className="md:w-4 md:h-5" />
                  Above Average
                </div>
              </div>
              <div
                className={clsx(
                  'absolute top-[104px] left-10 py-2 px-3 bg-white rounded-[4px]',
                  'md:top-[180px] md:left-20 md:py-4 md:px-6 md:rounded-lg'
                )}
              >
                <div className="text-[10px] text-alter-black md:text-lg">Ease of Weight Loss</div>
                <div className="uppercase text-[8px] text-alter-black-60 md:text-sm md:mt-1">
                  DNA SNP: rs10830963, rs1175544, +4
                </div>
                <div className="flex gap-1 mt-2 text-[8px] md:text-base md:gap-2 md:mt-5">
                  <Image
                    src={Lightning}
                    alt="lightning"
                    width={9}
                    height={10}
                    className="md:w-4 md:h-5"
                  />
                  Above Average
                </div>
              </div>
              <MotionImage
                ref={swabRef}
                initial={{ opacity: 0, translateY: 160 }}
                src={AlterSwab}
                alt="Alter swab"
                draggable={false}
                className={clsx(
                  'absolute bottom-0 -right-1/4 object-contain sm:right-0',
                  'max-h-[90%]'
                )}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 mt-6 md:grid-cols-2 md:basis-1/2 md:mt-0">
            {CARDS_TEXT.map(({ alt, icon, text, title }) => (
              <div key={alt} className="p-8 pb-12 rounded-3xl bg-alter-beige md:pb-8">
                <Image src={icon} alt={alt} width={32} height={32} />
                <div className="mt-8 text-alter-black-60 uppercase md:mt-10">{title}</div>
                {text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DrivenByScience;
